import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const FIELDS = ["any","Business","Engineering","Computer Science","Fashion","Psychology","Humanities","Political Science","Medicine"];
const CITIES = ["any","Paris","Lyon","Bordeaux","Toulouse","Nice","Marseille","Strasbourg","Nantes","Grenoble","Rennes","Lille","Aix-en-Provence","Montpellier","Cergy","Palaiseau","Fontainebleau","Jouy-en-Josas"];

export default function Plan() {
  const [unis, setUnis] = useState([]);
  const [field, setField] = useState("any");
  const [city, setCity] = useState("any");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/universities", { params: { field, city } });
        setUnis(data);
      } finally { setLoading(false); }
    })();
  }, [field, city]);

  return (
    <div className="pt-28 md:pt-32 pb-24 min-h-screen" data-testid="page-plan">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="rule-gold" /><span className="eyebrow">02 · Plan</span>
        </div>
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <h1 className="md:col-span-7 serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Twenty schools. <em className="italic text-[#b8892b]">One shortlist</em>.
          </h1>
          <p className="md:col-span-5 text-lg text-[#4a5570] leading-relaxed">
            Filter our curated catalog by field and city. Every school below is verified: real tuition figures for
            2026–27, honest highlights, and campus context.
          </p>
        </div>

        <div className="mt-12 flex flex-wrap gap-3 items-center border-y border-[#0d1b3e]/15 py-4" data-testid="plan-filters">
          <div className="eyebrow text-[#0d1b3e]">Filter</div>
          <select value={field} onChange={(e) => setField(e.target.value)} data-testid="filter-field"
            className="border border-[#0d1b3e]/20 bg-white px-3 py-2 text-sm">
            {FIELDS.map(f => <option key={f} value={f}>{f === "any" ? "Any field" : f}</option>)}
          </select>
          <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="filter-city"
            className="border border-[#0d1b3e]/20 bg-white px-3 py-2 text-sm">
            {CITIES.map(c => <option key={c} value={c}>{c === "any" ? "Any city" : c}</option>)}
          </select>
          <div className="ml-auto text-sm text-[#4a5570]">{loading ? "Loading…" : `${unis.length} schools`}</div>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="plan-uni-grid">
          {unis.map((u, i) => (
            <motion.article key={u.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
              className="bg-white border border-[#0d1b3e]/10 group hover:shadow-lg transition-shadow"
              data-testid={`uni-card-${u.slug}`}>
              <div className="aspect-[16/10] overflow-hidden relative">
                <img src={u.hero} alt={u.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" />
                <div className="absolute top-3 left-3 bg-[#0d1b3e] text-white text-[10px] uppercase tracking-[0.2em] px-2 py-1">
                  {u.city}
                </div>
                {u.english_taught && (
                  <div className="absolute top-3 right-3 bg-[#b8892b] text-white text-[10px] uppercase tracking-[0.18em] px-2 py-1">
                    English-taught
                  </div>
                )}
              </div>
              <div className="p-6">
                <div className="serif text-2xl leading-tight text-[#0d1b3e]">{u.name}</div>
                <div className="text-sm text-[#4a5570] mt-1">Est. {u.founded} · {u.level === "master" ? "Master's" : "Bachelor's"}</div>
                <p className="text-sm text-[#0d1b3e]/80 mt-3 leading-relaxed">{u.highlight}</p>
                <div className="mt-5 flex items-end justify-between border-t border-[#0d1b3e]/10 pt-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#4a5570]">Tuition / year</div>
                    <div className="serif text-2xl text-[#b8892b]">€{u.tuition_eur.toLocaleString()}</div>
                  </div>
                  <div className="text-xs text-[#4a5570] text-right max-w-[45%]">
                    {u.fields.slice(0,3).join(" · ")}
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-16 border-t border-[#0d1b3e]/15 pt-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="eyebrow mb-2">Not sure which fits?</div>
            <div className="serif text-3xl md:text-4xl text-[#0d1b3e] max-w-xl">Let our advisor narrow it to your top 3–5 in under two minutes.</div>
          </div>
          <Link to="/advisor" className="btn-sharp btn-sharp-gold" data-testid="plan-cta-advisor">
            Run the intake <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
